<!--
  This is for finding a restaurant via places api search
  selected restaurant is checked against our internal database
  options are then given to add if not there or use if it is.

-->

<script setup>
import { ref } from 'vue'
import restaurntPin1 from '@/assets/images/maps/restaurant-waiter-icon.svg'
import {useReservationsStore, useRestaurantsStore, useUsersStore} from "@/stores"
import { mediaUrl } from '@core/utils/mediaUrl'
import CmsText from '@/@core/components/CmsText.vue'
import {useMessagingStore} from '@/stores/messaging.store'
import {
  emailValidator,
  requiredValidator,
} from '@validators'

import {useRoute} from "vue-router"
const props = defineProps({
  restaurantName: {
    type: String,
    required: true
  },
  restaurantMarket: {
    type: String,
    required: true
  },

  // Optional: pass the restaurant object directly to avoid store lookup
  restaurant: {
    type: Object,
    default: null
  },
  button: {
    type: Boolean,
    default: false
  },
  showCount: {
    type: Boolean,
    default: true
  },
})
const emit = defineEmits([
  'update:isDialogVisible',
  'confirm',
  'cancel'
])
const route = useRoute()
const router = useRouter()

const usersStore = useUsersStore()
let myUser = null
let loginDialogMode = null;
({ myUser, loginDialogMode } = storeToRefs(usersStore))

const restaurantsStore = useRestaurantsStore()
let restaurants = null
let markets = [];
({ restaurants, markets } = storeToRefs(restaurantsStore))

//restaurantsStore.getRestaurants()


const reservationsStore = useReservationsStore()
const messagingStore = useMessagingStore()

const messagingUser = ref(null)

async function startMessage(user) {
  messagingUser.value = user._id
  try {
    const conv = await messagingStore.createConversation({
      type: 'member-member',
      memberIds: [user._id],
    })
    isFavoritedByOpen.value = false
    router.push({ path: '/messages', query: { conv: conv._id } })
  } catch (err) {
    console.error('startMessage error', err)
  } finally {
    messagingUser.value = null
  }
}



// A dummy guest (guest booking flow) is NOT a member — gate the fans list
// for them exactly like a logged-out visitor
const isRealMember = computed(() => !!myUser.value && myUser.value.userLevel !== 'dummy')

const theRestaurant = computed(() => {
  // Use passed restaurant prop if available (avoids store lookup issues)
  if (props.restaurant) {
    return props.restaurant
  }

  // Fall back to store lookup
  return restaurants.value.find(restaurant =>
    restaurant.name == props.restaurantName && restaurant.market == props.restaurantMarket
  )
})

const toolTip = computed(() => {
  if(isRealMember.value){
    return (favorited.value ? 'Remove Favorite' : 'Add to Favorites')
  } else{
    return 'Login to Favorite'
  }
})

const awaitingFavorite = ref(false)
const awaitingUnfavorite = ref(false)

const favorited = computed(() => {
  if(awaitingFavorite.value) return true
  if(awaitingUnfavorite.value) return false
  if(myUser?.value?._id) {
    // Check isFavorited first (set by search endpoint)
    if (theRestaurant?.value?.isFavorited) return true

    // Check favoritedBy array - need to convert ObjectIds to strings for comparison
    const favoritedBy = theRestaurant?.value?.favoritedBy
    if (favoritedBy && Array.isArray(favoritedBy)) {
      return favoritedBy.some(id => id?.toString() === myUser.value._id?.toString())
    }
  }
  
  return false
})

const restaurantId = computed(() => theRestaurant.value?._id || theRestaurant.value?.id)

// The store updates the restaurant objects it owns (lists, search results),
// but a restaurant passed in as a prop from elsewhere — the AI-chat card
// payload — is not one of those, so after the request resolved the heart
// read the stale isFavorited flag and un-filled itself (the "+" in chat
// looked like it did nothing). Keep that object honest here; the count is
// bumped only when the store did not already refresh it.
const syncPassedRestaurant = (isFavorited, countBefore) => {
  const r = props.restaurant
  if (!r) return
  r.isFavorited = isFavorited
  if (typeof r.favoriteCount === 'number' && r.favoriteCount === countBefore) {
    r.favoriteCount = Math.max(0, r.favoriteCount + (isFavorited ? 1 : -1))
  }
}

const toggleFavorite = async () => {
  // Real members only — "authenticated ≠ member": guest-booking-flow dummy
  // sessions carry a valid user, and letting them through here favorited
  // restaurants (and bumped public counts) from prospect taps. Guests and
  // dummies get the Inquire / Request Access dialog instead.
  if(isRealMember.value){
    if(favorited.value){
      // Currently favorited, so unfavorite
      awaitingUnfavorite.value = true
      const countBefore = props.restaurant?.favoriteCount
      await restaurantsStore.unfavoriteRestaurant(restaurantId.value, myUser.value._id)
      syncPassedRestaurant(false, countBefore)
      awaitingUnfavorite.value = false
    } else {
      // Currently not favorited, so favorite
      awaitingFavorite.value = true
      const countBefore = props.restaurant?.favoriteCount
      await restaurantsStore.favoriteRestaurant(restaurantId.value, myUser.value._id)
      syncPassedRestaurant(true, countBefore)
      awaitingFavorite.value = false
    }
  } else{
    loginDialogMode.value = 'activate'
  }
}

// Favorited-by bottom sheet
const isFavoritedByOpen = ref(false)
const favoritedByUsers = ref([])
const favoritedByLoading = ref(false)

const openFavoritedBy = async () => {
  isFavoritedByOpen.value = true
  if (!isRealMember.value) {
    return
  }
  favoritedByLoading.value = true
  const users = await restaurantsStore.getFavoritedBy(restaurantId.value)
  users.sort((a, b) => {
    const aIsMe = a._id === myUser.value?._id ? 0 : 1
    const bIsMe = b._id === myUser.value?._id ? 0 : 1
    if (aIsMe !== bIsMe) return aIsMe - bIsMe

    const socialRank = userId => {
      if (isFriend(userId)) return 0
      if (isFollowedOrPending(userId)) return 1

      return 2
    }

    const aSocialRank = socialRank(a._id)
    const bSocialRank = socialRank(b._id)
    if (aSocialRank !== bSocialRank) return aSocialRank - bSocialRank

    // platinum + image (0), platinum no image (1), everyone else (2)
    const platinumTier = u => showPlatinumBadge(u) ? (u.profileImageId ? 0 : 1) : 2
    
    return platinumTier(a) - platinumTier(b)
  })
  favoritedByUsers.value = users
  favoritedByLoading.value = false
}

const viewingDetailsMember = ref(null)

const handleViewMember = async user => {
  isFavoritedByOpen.value = false
  await usersStore.fetchUserById(user._id)
  const fullUser = usersStore.users.find(u => u._id === user._id)
  viewingDetailsMember.value = fullUser || user
}

const handleMemberDialogClose = () => {
  viewingDetailsMember.value = null
  isFavoritedByOpen.value = true
}

const showPlatinumBadge = user => {
  return user?.isPlatinum || user?.membershipType === 'Platinum' || user?.membershipType === 'Trial'
}

const isFollowedOrPending = userId => {
  if (!myUser.value?.following) return false

  return myUser.value.following.some(id => {
    const idStr = typeof id === 'object' ? id._id || id.id || id : id

    return idStr === userId || idStr?.toString() === userId?.toString()
  })
}

const isFriend = userId => {
  if (!myUser.value?.followersApproved) return false

  return myUser.value.followersApproved.some(id => {
    const idStr = typeof id === 'object' ? id._id || id.id || id : id

    return idStr === userId || idStr?.toString() === userId?.toString()
  })
}

const handleFollow = async user => {
  await usersStore.follow(user._id)
}

const handleUnfollow = async user => {
  await usersStore.unfollow(user._id)
}

// toggleFavorite is exposed so sibling controls (the AI-chat row's "+")
// can delegate favoriting to this component instead of reimplementing it
defineExpose({ openFavoritedBy, toggleFavorite })
</script>

<template>
  <template v-if="theRestaurant">
    <template v-if="button">
      <VBtn
        color="orange-lighten-2"
        variant="text"
        @click.stop="toggleFavorite"
      >
        <VIcon
          size="16"
          class="mr-1 no-outline"
          :icon="(favorited ? 'mdi-heart' : 'mdi-heart-outline')"
          :color="(favorited ? 'primary-dark' : '')"
        />
        <span v-if="favorited">Remove Favorite</span>
        <span v-else>Add to Favorites</span>
      </VBtn>
    </template>

    <template v-else>
      <VTooltip
        :text="toolTip"
        open-on-click
      >
        <template #activator="{ props }">
          <VIcon
            size="16"
            class="no-outline"
            :icon="(favorited ? 'mdi-heart' : 'mdi-heart-outline')"
            :color="(favorited ? 'primary-dark' : '')"
            v-bind="props"
            @click.stop="toggleFavorite"
          />

          <span
            v-if="showCount && theRestaurant?.favoriteCount > 0"
            class="favorite-count clickable-count"
            @click.stop="openFavoritedBy"
          >{{ theRestaurant.favoriteCount }}</span>
        </template>
      </VTooltip>
    </template>

    <!-- Favorited By Bottom Sheet -->
    <VBottomSheet v-model="isFavoritedByOpen">
      <VCard class="favorited-by-sheet">
        <div class="sheet-handle" />
        <div class="sheet-header">
          <span class="sheet-title">Members who are Fans of {{ theRestaurant?.name }} ({{ isRealMember ? favoritedByUsers.length : theRestaurant?.favoriteCount }})</span>
          <VBtn
            icon
            variant="text"
            size="small"
            @click="isFavoritedByOpen = false"
          >
            <VIcon size="20">
              mdi-close
            </VIcon>
          </VBtn>
        </div>
        <VDivider />
        <div class="sheet-body">
          <!--
            Not logged in: one calm serif gate line (no login CTA —
            same treatment as the gated discovery tabs) 
          -->
          <div
            v-if="!isRealMember"
            class="fans-gated"
          >
            <CmsText
              k="restaurant.fans.gatedLine"
              tag="p"
              class="fans-gate-line"
            />
          </div>

          <!-- Loading skeletons -->
          <div v-else-if="favoritedByLoading">
            <VListItem
              v-for="i in 3"
              :key="'skeleton-' + i"
              class="pl-3 pr-3"
            >
              <VSkeletonLoader type="list-item-avatar-two-line" />
            </VListItem>
          </div>

          <!-- Empty state -->
          <div
            v-else-if="favoritedByUsers.length === 0"
            class="text-center text-medium-emphasis py-6"
          >
            No members yet
          </div>

          <!-- User list -->
          <div v-else>
            <VListItem
              v-for="user in favoritedByUsers"
              :key="user._id"
              class="pl-3 pr-3 fans-user-row"
              @click="handleViewMember(user)"
            >
              <template #prepend>
                <VAvatar
                  v-if="user.profileImageId"
                  size="48"
                  class="list-avatar"
                >
                  <VImg
                    :src="mediaUrl(user.profileImageId, 'thumb')"
                    :alt="user.firstName"
                  />
                </VAvatar>
                <VAvatar
                  v-else
                  size="48"
                  class="list-avatar avatar-initials"
                >
                  <span class="avatar-fallback-text">{{ user?.firstName?.[0]?.toUpperCase() }}{{ user?.lastName?.[0]?.toUpperCase() }}</span>
                </VAvatar>
              </template>

              <template #title>
                <span class="text-sm name-line">
                  {{ user.firstName }} {{ user.lastName }}
                  <VIcon
                    v-if="user.verified"
                    size="14"
                    class="verified-check"
                  >
                    material-symbols:verified
                  </VIcon>
                  <template v-if="showPlatinumBadge(user)">
                    <span class="text-xs platinum-label">
                      (Platinum)
                    </span>
                  </template>
                </span>
              </template>
              <template #subtitle>
                <div class="text-xs">
                  {{ user.company ? user.company : '' }}
                  <div
                    v-if="user.favoritedRestaurantsCount > 0"
                    class="text-medium-emphasis"
                  >
                    {{ user.favoritedRestaurantsCount }} Favorites
                  </div>
                </div>
              </template>
              <template
                v-if="user.hasSharedHighlight || user._id !== myUser?._id"
                #append
              >
                <div
                  class="append-actions"
                  @click.stop
                >
                  <div class="social-action-wrap">
                    <VTooltip
                      v-if="user.hasSharedHighlight"
                      :text="`${user.firstName} shared a highlight about this restaurant`"
                      location="top"
                    >
                      <template #activator="{ props: tooltipProps }">
                        <VBtn
                          variant="tonal"
                          size="small"
                          color="primary"
                          class="icon-square-btn"
                          v-bind="tooltipProps"
                        >
                          <VIcon
                            size="18"
                            icon="mdi-note-text"
                          />
                        </VBtn>
                      </template>
                    </VTooltip>
                    <template v-if="user._id !== myUser?._id">
                      <VBtn
                        v-if="!isFollowedOrPending(user._id)"
                        variant="tonal"
                        size="small"
                        class="icon-square-btn"
                        aria-label="Add Friend"
                        @click="handleFollow(user)"
                      >
                        <VIcon
                          size="18"
                          icon="mdi-account-plus"
                        />
                      </VBtn>
                      <VBtn
                        v-else-if="isFriend(user._id)"
                        variant="tonal"
                        size="small"
                        color="primary"
                        class="icon-square-btn"
                        :loading="messagingUser === user._id"
                        @click="startMessage(user)"
                      >
                        <VIcon
                          size="18"
                          icon="mdi-message-text-outline"
                        />
                      </VBtn>
                      <VBtn
                        v-else
                        variant="tonal"
                        size="small"
                        color="secondary"
                        class="social-list-btn"
                        @click="handleUnfollow(user)"
                      >
                        Rescind
                      </VBtn>
                    </template>
                  </div>
                </div>
              </template>
            </VListItem>
          </div>
        </div>
      </VCard>
    </VBottomSheet>

    <!-- Member Profile Dialog -->
    <MemberDetailsDialog
      :member="viewingDetailsMember"
      @on-close="handleMemberDialogClose"
    />
  </template>
</template>

<style scoped>
.no-outline {
  outline: none;
}

/* Heart + count are one unit: the count hugs the heart; the unit centers
   vertically against the restaurant name (no baseline nudges) */
.favorite-count {
  display: inline-block;
  font-weight: bold;
  color: var(--ds-text-strong);
  margin-left: 2px;
  padding: var(--ds-space-1) 2px;
  font-size: var(--ds-text-base); /* 14px, per the master type scale */
  line-height: 1;
  vertical-align: middle;
}

.clickable-count {
  cursor: pointer;
}

.clickable-count:hover {
  color: var(--ds-brand-primary-dark, rgb(var(--v-theme-primary)));
}

.favorited-by-sheet {
  border-radius: var(--ds-radius-lg) var(--ds-radius-lg) 0 0 !important;
  block-size: 70vh;
  display: flex;
  flex-direction: column;
}

/* Logged-out gate — same calm serif treatment as the gated discovery tabs */
.fans-gated {
  padding: var(--ds-space-10) var(--ds-space-6) var(--ds-space-16);
  text-align: center;
}

.fans-gate-line {
  font-family: var(--ds-font-heading);
  font-weight: var(--ds-font-medium);
  font-size: var(--ds-text-lg);
  line-height: var(--ds-leading-snug);
  color: var(--ds-brand-ink);
  margin: 0 auto;
}

.sheet-handle {
  width: 36px;
  height: 4px;
  border-radius: var(--ds-radius-xs);
  background: rgba(var(--v-theme-on-surface), 0.2);
  margin: 10px auto 4px;
}

.sheet-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 16px 8px 20px;
}

.sheet-title {
  font-size: var(--ds-text-md);
  font-weight: 600;
  color: rgba(var(--v-theme-on-surface), 0.87);
}

.sheet-body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding-block-end: var(--ds-space-4);
}

.list-avatar {
  margin-inline-end: 2px;
  margin-left: 2px;
}

:deep(.fans-user-row .v-list-item__prepend) {
  width: auto;
}

:deep(.fans-user-row .v-list-item__spacer) {
  width: 4px;
}

.avatar-initials {
  background: #e9f4f3;
  outline: thin solid rgba(69, 182, 169, 0.25);
  outline-offset: 2px;
}

.avatar-fallback-text {
  font-size: 1rem;
  font-weight: 500;
}

.name-line .verified-check {
  margin-left: 0px;
  margin-top: -2px;
  color: rgb(0, 149, 246);
}

.name-line .platinum-label {
  margin-left: 0px;
}

.social-list-btn {
  width: 100px;
  min-height: 28px !important;
  height: 28px !important;
  padding: var(--ds-space-1) 14px !important;
}

.append-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
}

.social-action-wrap {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 20px;
}

.icon-square-btn {
  min-width: 32px !important;
  width: 32px !important;
  min-height: 32px !important;
  height: 32px !important;
  padding: 0 !important;
  border-radius: var(--ds-radius-sm) !important;
}
</style>
